import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const formatValue = (metricKey, value, currency = 'USD') => {
  if (value === null || value === undefined) return 'N/A';
  
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', CAD: 'C$', AUD: 'A$'
  };
  
  const symbol = currencySymbols[currency] || '$';
  
  if (metricKey === 'mrr' || metricKey === 'cac' || metricKey === 'ltv' || metricKey === 'burn_rate') {
    if (currency === 'JPY') {
      return `${symbol}${Math.round(value).toLocaleString('en-US')}`;
    }
    return `${symbol}${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  } else if (metricKey === 'churn' || metricKey === 'growth' || metricKey === 'pmf') {
    return `${value.toFixed(2)}%`;
  } else if (metricKey === 'runway') {
    return `${value.toFixed(1)} months`;
  }
  return value.toString();
};

const MetricTimeline = ({ startupId, currency }) => {
  const [timeline, setTimeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedMetrics, setExpandedMetrics] = useState({});

  useEffect(() => {
    if (startupId) {
      loadTimeline();
    }
  }, [startupId]);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/startups/${startupId}/weekly-timeline`, {
        withCredentials: true
      });
      setTimeline(response.data);
      
      // Expand first metric by default
      const firstMetric = Object.keys(response.data)[0];
      if (firstMetric) {
        setExpandedMetrics({ [firstMetric]: true });
      }
    } catch (error) {
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const toggleMetric = (metricKey) => {
    setExpandedMetrics({
      ...expandedMetrics,
      [metricKey]: !expandedMetrics[metricKey]
    });
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/40 border-slate-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-400"></div>
        </div>
      </Card>
    );
  }

  if (Object.keys(timeline).length === 0) {
    return (
      <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="timeline-empty">
        <div className="text-center py-8">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400">No weekly data yet. Submit your first week's data above!</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="metric-timeline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-slate-100">Weekly Timeline</h3>
        <p className="text-sm text-slate-400">Track your metrics week by week</p>
      </div>

      {Object.entries(timeline).map(([metricKey, data]) => {
        const isExpanded = expandedMetrics[metricKey];
        const entries = data.entries || [];
        const missingWeeks = data.missing_weeks || [];
        
        return (
          <Card key={metricKey} className="bg-slate-800/40 border-slate-700" data-testid={`timeline-${metricKey}`}>
            {/* Header */}
            <button
              onClick={() => toggleMetric(metricKey)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="font-semibold text-slate-100">{data.metric_name}</span>
                <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full">
                  {entries.length} weeks
                </span>
                {missingWeeks.length > 0 && (
                  <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/30">
                    {missingWeeks.length} missing
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {/* Timeline Entries */}
            {isExpanded && (
              <div className="px-6 pb-4 border-t border-slate-700">
                <div className="mt-4 space-y-2">
                  {/* Show entries and missing weeks merged and sorted */}
                  {[...entries.map(e => ({...e, type: 'entry'})), ...missingWeeks.map(m => ({...m, type: 'missing'}))]
                    .sort((a, b) => {
                      if (a.year !== b.year) return b.year - a.year;
                      return b.week_number - a.week_number;
                    })
                    .map((item, idx) => {
                      if (item.type === 'missing') {
                        return (
                          <div
                            key={`missing-${item.year}-${item.week_number}`}
                            className="flex items-center space-x-3 py-2 px-3 rounded bg-red-500/5 border border-red-500/20"
                            data-testid={`missing-week-${item.week_number}`}
                          >
                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="text-slate-300 font-medium">Week {item.week_number}, {item.year}</span>
                              <span className="text-red-400 text-sm ml-3">No data</span>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={`entry-${item.year}-${item.week_number}`}
                          className="flex items-center space-x-3 py-2 px-3 rounded hover:bg-slate-700/20"
                          data-testid={`week-entry-${item.week_number}`}
                        >
                          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                          <div className="flex-1 flex items-center justify-between">
                            <div>
                              <span className="text-slate-300 font-medium">Week {item.week_number}, {item.year}</span>
                              {item.calculated && (
                                <span className="text-cyan-400 font-semibold ml-3">
                                  {formatValue(metricKey, item.value, currency)}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-slate-500">
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default MetricTimeline;
